import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingMasterType, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb, warehousingMasterTypeDescription, warehousingMasterTypeLabel, warehousingMasterTypeRoute } from '../warehousing-common';
import { WarehousingMasterData } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';

@Component({
  selector: 'app-warehousing-setup-view',
  standalone: true,
  imports: [CommonModule, RouterModule, BreadcrumbModule, ButtonModule, ProgressBarModule, TagModule, PageDetailHeroComponent, PageFormSectionComponent, PageMetricStripComponent, PageShellComponent],
  templateUrl: './warehousing-setup-view.component.html',
  styleUrl: './warehousing-setup-view.component.css'
})
export class WarehousingSetupViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);

  isLoading = false;
  type: WarehousingMasterType = 'REGION';
  title = 'Region';
  setupDescription = 'Dedicated setup value record.';
  listRoute = '/setup/regions';
  isStandaloneContext = false;
  row?: WarehousingMasterData;
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  ngOnInit(): void {
    const routeType = this.route.snapshot.data['masterType'] as WarehousingMasterType;
    this.type = routeType;
    this.isStandaloneContext = this.route.snapshot.data['standaloneContext'] === true;
    this.title = warehousingMasterTypeLabel(this.type);
    this.setupDescription = warehousingMasterTypeDescription(this.type);
    this.listRoute = routeType === 'CATEGORY' ? warehousingMasterTypeRoute('CATEGORY') : warehousingMasterTypeRoute(this.type);
    this.breadcrumbItems = this.isStandaloneContext
      ? [{ label: 'Setup' }, { label: this.title, routerLink: this.listRoute }, { label: 'Details' }]
      : warehousingBreadcrumb('Details', this.title, this.listRoute);
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    this.warehousingService.getMasterData(this.type).subscribe({
      next: response => {
        this.row = (response.data || []).find(item => item.id === id);
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  statusSeverity = statusSeverity;

  get showLocation(): boolean {
    return this.type === 'WAREHOUSE';
  }

  get showSupplier(): boolean {
    return this.type === 'WAREHOUSE';
  }

  get showRegionMappings(): boolean {
    return this.type === 'REGION' && !!this.row?.regionMappings?.length;
  }

  get showDefaultWarehouse(): boolean {
    return this.type === 'REGION';
  }

  get showRelationship(): boolean {
    return ['WAREHOUSE', 'ZONE', 'CATEGORY', 'BRAND', 'SUB_BRAND'].includes(this.type);
  }

  metrics(): PageMetric[] {
    const metrics: PageMetric[] = [
      { label: 'Type', value: this.title },
      { label: 'Code', value: this.row?.code || '-' },
      { label: 'Status', value: this.row?.isActive === false ? 'INACTIVE' : 'ACTIVE', tone: this.row?.isActive === false ? 'danger' : 'success' }
    ];
    if (this.showRelationship) {
      metrics.push({ label: 'Relationship', value: this.relationshipLabel() });
    }
    if (this.showSupplier) {
      metrics.push({ label: 'Supplier', value: this.supplierLabel() });
    }
    if (this.showRegionMappings) {
      metrics.push({ label: 'Covered cities', value: String(this.row?.regionMappings?.length || 0) });
    }
    if (this.showDefaultWarehouse) {
      metrics.push({ label: 'Default warehouse', value: this.defaultWarehouseLabel() });
    }
    return metrics;
  }

  relationshipLabel(): string {
    if (!this.row) return '-';
    if (this.row.hierarchyPath) return this.row.hierarchyPath;
    if (this.type === 'CATEGORY') {
      if (this.row.parentId || this.row.parentName) {
        return this.row.parentName || '-';
      }
      return [this.row.crmClientName, this.row.crmAccountName].filter(Boolean).join(' > ') || '-';
    }
    return this.row.parentName || '-';
  }

  supplierLabel(): string {
    if (!this.row) return '-';
    return [this.row.supplierClientName, this.row.supplierWarehouseName].filter(Boolean).join(' / ') || '-';
  }

  ownershipLabel(row: WarehousingMasterData): string {
    if (row.warehouseOwnershipType === 'THIRD_PARTY_VIRTUAL') return 'Third-party virtual (non-stock)';
    if (row.warehouseOwnershipType === 'THIRD_PARTY_MANAGED') return 'Third-party managed cold store';
    return 'Company owned';
  }

  defaultWarehouseLabel(): string {
    if (!this.row) return '-';
    return this.row.defaultWarehouseName || (this.row.defaultWarehouseId ? `Warehouse #${this.row.defaultWarehouseId}` : 'Not set');
  }

  regionMappingLabel(index: number): string {
    const mapping = this.row?.regionMappings?.[index];
    if (!mapping) return '-';
    return [mapping.countryName, mapping.cityName].filter(Boolean).join(' / ') || '-';
  }

  editQuery(): Record<string, string> | null {
    return null;
  }
}
